/**
 * รับรองกงสุล (MFA legalisation) — Q&A Wave 2 (deep-dive, curated).
 *
 * ACCURACY POLICY (ตรวจทาน 2026-08-03)
 * - ผู้ให้บริการนิติกรณ์: กองสัญชาติและนิติกรณ์ กรมการกงสุล กระทรวงการต่างประเทศ
 *   ถนนแจ้งวัฒนะ — ค่าธรรมเนียม 200 บาท/ตราประทับ (ปกติ) และ 400 บาท/ตราประทับ (ด่วน)
 *   อ้างอิง consular.mfa.go.th
 * - สถานะ Apostille ของไทยอ้างจาก src/data/legal-facts.ts เท่านั้น
 *   (อนุสัญญามีผลกับไทย 28 ก.พ. 2027 — ก่อนหน้านั้นไทยยังไม่ออกตรา Apostille)
 * - ห้ามระบุค่าบริการของเรา · ห้ามระบุผลล่วงหน้า · เวลาเป็นช่วงโดยประมาณ
 */
import type { LgzQA } from "./questions-500";
import { APOSTILLE_STATUS_SUMMARY_TH } from "@/data/legal-facts";

const TAIL =
  "ข้อมูล ณ ส.ค. 2026 — ระเบียบของกรมการกงสุลและของสถานทูตปลายทางเปลี่ยนแปลงได้ โปรดยืนยันเช็กลิสต์กับหน่วยงานผู้รับเอกสารก่อนยื่นทุกครั้ง";
const CTA =
  "สอบถามค่าบริการและรายการเอกสารเฉพาะเคสได้ทางโทรศัพท์ LINE หรืออีเมล";
const FEE =
  "ค่าธรรมเนียมนิติกรณ์เป็นอัตราของกรมการกงสุล 200 บาทต่อตราประทับแบบปกติ และ 400 บาทต่อตราประทับแบบด่วน";

export const LGZ_QA_WAVE2: LgzQA[] = [
  {
    slug: "lgz-w2-สายรับรองที่ถูกต้อง",
    q: "ลำดับการรับรองเอกสารที่ถูกต้องคืออะไร ทำผิดลำดับแล้วเกิดอะไรขึ้น?",
    aShort:
      "ลำดับมาตรฐานคือ ต้นฉบับจากหน่วยงานผู้ออก → (เอกสารเอกชนผ่าน Notary ก่อน) → แปล → นิติกรณ์กรมการกงสุล → สถานทูตปลายทางถ้าปลายทางยังกำหนด ทำสลับขั้นตอนมักถูกตีกลับทั้งชุด",
    aLong:
      "กรมการกงสุลรับรองลายมือชื่อและตราประทับของเจ้าหน้าที่ผู้ออกเอกสาร ไม่ได้รับรองเนื้อหา จึงต้องมีต้นฉบับที่คัดใหม่จากหน่วยงานผู้ออกก่อนเสมอ เอกสารเอกชนอย่างหนังสือมอบอำนาจหรือคำให้การต้องให้ทนายผู้ทำคำรับรองลายมือชื่อรับรองก่อน แล้วจึงเข้าสายนิติกรณ์ · หากนำไปยื่นสถานทูตก่อนผ่านนิติกรณ์ สถานทูตส่วนใหญ่จะปฏิเสธเพราะไม่มีตราของกระทรวงการต่างประเทศให้เทียบลายมือชื่อ · " +
      FEE + " · " + CTA + " · " + TAIL,
    synonyms: ["ลำดับรับรองเอกสารไปต่างประเทศ", "order of legalization thailand", "ต้องทำ notary ก่อนกงสุลไหม"],
    cluster: "process",
  },
  {
    slug: "lgz-w2-apostille-สถานะไทย",
    q: "ตอนนี้ขอ Apostille ที่ไทยได้หรือยัง?",
    aShort:
      "ยังไม่ได้ ไทยยังไม่ออกตรา Apostille จนกว่าอนุสัญญาจะมีผลบังคับใช้กับไทยในวันที่ 28 กุมภาพันธ์ 2027 ระหว่างนี้ยังใช้สายนิติกรณ์กรมการกงสุลตามเดิม",
    aLong:
      APOSTILLE_STATUS_SUMMARY_TH +
      " · สิ่งที่ควรทำระหว่างนี้คือวางแผนเอกสารตามสายเดิม และอย่ารอ Apostille ถ้ากำหนดยื่นอยู่ก่อนวันที่อนุสัญญามีผล เพราะเอกสารที่ออกและรับรองตามสายเดิมยังใช้ได้ต่อไปตามเงื่อนไขของปลายทาง · " +
      CTA + " · " + TAIL,
    synonyms: ["apostille ไทยเริ่มเมื่อไหร่", "thailand apostille start date", "ขอ apostille ที่กรมการกงสุลได้ไหม"],
    cluster: "compare",
  },
  {
    slug: "lgz-w2-สาเหตุถูกตีกลับ",
    q: "เอกสารถูกกรมการกงสุลตีกลับเพราะอะไรบ่อยที่สุด?",
    aShort:
      "สาเหตุที่พบบ่อยคือ สะกดชื่อในคำแปลไม่ตรงหนังสือเดินทาง ใช้สำเนาเก่าที่ไม่ได้คัดใหม่ ตราประทับหรือลายมือชื่อเจ้าหน้าที่ไม่ชัด และคำแปลตกข้อความบางส่วนของต้นฉบับ",
    aLong:
      "ทางแก้เชิงระบบคือถ่ายสำเนาหน้าหนังสือเดินทางแนบไปกับงานแปลทุกครั้งเพื่อให้ผู้แปลสะกดชื่อตรงกัน คัดเอกสารทะเบียนราษฎรใหม่จากอำเภอหรือเขตเมื่อฉบับเดิมเก่ากว่าที่ปลายทางกำหนด และตรวจว่าคำแปลครอบคลุมทุกบรรทัดรวมถึงข้อความบนตราประทับ · หากถูกตีกลับ ให้เก็บใบแจ้งเหตุผลไว้ เพราะการยื่นรอบใหม่จะเร็วขึ้นมากเมื่อแก้ตรงประเด็น · " +
      CTA + " · " + TAIL,
    synonyms: ["กงสุลไม่รับเอกสาร", "เอกสารตีกลับกรมการกงสุล", "mfa legalization rejected reason"],
    cluster: "problem",
  },
  {
    slug: "lgz-w2-มอบอำนาจยื่นแทน",
    q: "ให้คนอื่นยื่นนิติกรณ์แทนได้ไหม ต้องใช้อะไรบ้าง?",
    aShort:
      "ยื่นแทนได้ โดยผู้ยื่นต้องมีหนังสือมอบอำนาจ สำเนาบัตรประชาชนหรือหนังสือเดินทางของผู้มอบอำนาจพร้อมลงชื่อรับรองสำเนา และบัตรประจำตัวของผู้รับมอบอำนาจ",
    aLong:
      "กรณีเจ้าของเอกสารอยู่ต่างประเทศ ให้ลงนามในหนังสือมอบอำนาจต่อหน้าเจ้าหน้าที่สถานเอกอัครราชทูตหรือสถานกงสุลใหญ่ไทยในประเทศนั้น แล้วส่งต้นฉบับกลับมาไทย · ควรระบุขอบเขตการมอบอำนาจให้ครอบคลุมทั้งการยื่น การรับเอกสารคืน และการยื่นต่อสถานทูตปลายทาง เพื่อไม่ต้องทำหนังสือมอบอำนาจใหม่กลางทาง · " +
      CTA + " · " + TAIL,
    synonyms: ["หนังสือมอบอำนาจยื่นกงสุล", "power of attorney mfa legalization", "ยื่นนิติกรณ์แทนได้ไหม"],
    cluster: "process",
  },
  {
    slug: "lgz-w2-ต่างจังหวัดส่งไปรษณีย์",
    q: "อยู่ต่างจังหวัดยื่นนิติกรณ์ทางไปรษณีย์ได้ไหม?",
    aShort:
      "ได้ กรมการกงสุลรับคำร้องทางไปรษณีย์ โดยส่งเอกสาร คำร้อง สำเนาบัตรประชาชน และซองตอบกลับตามที่ประกาศบนเว็บไซต์ consular.mfa.go.th แต่รอบเวลาจะนานกว่ายื่นด้วยตนเอง",
    aLong:
      "ข้อควรระวังคือเอกสารสูญหายระหว่างทาง จึงควรส่งแบบมีเลขติดตามและถ่ายภาพเอกสารทุกหน้าก่อนส่ง · หากมีกำหนดยื่นวีซ่าใกล้ ๆ การใช้ผู้ให้บริการเดินเรื่องที่รับ-ส่งเอกสารและยื่นด้วยตนเองแทนจะควบคุมเวลาได้ดีกว่า · " +
      FEE + " · " + CTA + " · " + TAIL,
    synonyms: ["ส่งเอกสารไปกงสุลทางไปรษณีย์", "นิติกรณ์ต่างจังหวัด", "mfa legalization by post thailand"],
    cluster: "location",
  },
  {
    slug: "lgz-w2-เอกสารอายุกี่เดือน",
    q: "เอกสารที่ผ่านนิติกรณ์แล้วมีอายุใช้งานนานแค่ไหน?",
    aShort:
      "ตราประทับนิติกรณ์เองไม่ได้กำหนดวันหมดอายุ แต่หน่วยงานปลายทางหลายแห่งกำหนดว่าเอกสารต้องออกหรือรับรองไม่เกิน 3 หรือ 6 เดือน จึงต้องยึดเงื่อนไขของปลายทางเป็นหลัก",
    aLong:
      "เอกสารกลุ่มที่ปลายทางมักกำหนดอายุคือใบรับรองความเป็นโสด ใบตรวจประวัติอาชญากรรม และหนังสือรับรองบริษัท ส่วนสูติบัตรหรือทะเบียนสมรสมักดูที่วันคัดสำเนาใหม่ · วิธีวางแผนคือนับถอยหลังจากวันนัดยื่นปลายทาง แล้วเริ่มกระบวนการให้เอกสารเสร็จก่อนนัดประมาณ 2–4 สัปดาห์ · " +
      CTA + " · " + TAIL,
    synonyms: ["เอกสารรับรองกงสุลหมดอายุไหม", "legalized document validity thailand", "ใบโสดอายุกี่เดือน"],
    cluster: "documents",
  },
  {
    slug: "lgz-w2-แปลภาษาอื่นนอกจากอังกฤษ",
    q: "กรมการกงสุลรับรองคำแปลภาษาอื่นนอกจากอังกฤษไหม?",
    aShort:
      "งานรับรองคำแปลของกรมการกงสุลเน้นคู่ภาษาไทย–อังกฤษเป็นหลัก หากปลายทางต้องการภาษาอื่น แนวทางที่ใช้ได้จริงคือทำคู่ไทย–อังกฤษให้ผ่านนิติกรณ์ก่อน แล้วแปลต่อเป็นภาษาปลายทางตามที่สถานทูตหรือหน่วยงานนั้นกำหนด",
    aLong:
      "สถานทูตหลายประเทศกำหนดให้แปลเป็นภาษาของตนโดยนักแปลที่สถานทูตรับรองหรือนักแปลสาบานตนในประเทศปลายทาง การทำคู่ไทย–อังกฤษไว้ก่อนจึงเป็นฐานที่ยืดหยุ่นที่สุด · ก่อนเริ่มงานควรถามปลายทางสองข้อคือ ต้องแปลโดยใคร และต้องรับรองกี่ชั้น · " +
      CTA + " · " + TAIL,
    synonyms: ["กงสุลรับรองคำแปลภาษาจีนไหม", "mfa certified translation language", "แปลภาษาอื่นรับรองกงสุล"],
    cluster: "documents",
  },
  {
    slug: "lgz-w2-สถานทูตต้องยื่นต่อไหม",
    q: "ผ่านนิติกรณ์แล้วยังต้องยื่นสถานทูตปลายทางอีกไหม?",
    aShort:
      "ขึ้นกับประเทศปลายทาง หลายประเทศกำหนดให้ประทับตราสถานทูตหรือสถานกงสุลของตนในไทยต่อจากนิติกรณ์ ขณะที่บางประเทศรับเอกสารที่ผ่านนิติกรณ์อย่างเดียว",
    aLong:
      "วิธีตรวจที่แม่นที่สุดคืออ่านหน้าเว็บไซต์ฝ่ายกงสุลของสถานทูตประเทศนั้นในประเทศไทย และเก็บหน้าเช็กลิสต์ไว้เป็นหลักฐาน เพราะเงื่อนไขเปลี่ยนได้ตามนโยบายวีซ่า · สถานทูตบางแห่งกำหนดให้ผู้ยื่นนัดหมายล่วงหน้าและรับเอกสารคืนเฉพาะวันที่กำหนด จึงควรบวกเวลาส่วนนี้ไว้ในแผน · " +
      CTA + " · " + TAIL,
    synonyms: ["ต้องยื่นสถานทูตหลังกงสุลไหม", "embassy legalization after mfa", "double legalization thailand"],
    cluster: "country",
  },
  {
    slug: "lgz-w2-ด่วนภายในวันเดียว",
    q: "งานด่วนภายในวันเดียวทำได้จริงไหม เงื่อนไขคืออะไร?",
    aShort:
      "กรมการกงสุลมีบริการแบบด่วนซึ่งคิดค่าธรรมเนียม 400 บาทต่อตราประทับ โดยต้องยื่นภายในช่วงเวลารับคำร้องด่วนของวันนั้น และเอกสารต้องครบถูกต้องตั้งแต่แรก",
    aLong:
      "ปัจจัยที่ทำให้งานด่วนพลาดเวลาไม่ใช่ค่าธรรมเนียม แต่คือเอกสารไม่ครบ คำแปลผิด หรือปริมาณคิวในวันนั้น จึงควรตรวจชุดเอกสารให้จบก่อนถึงเคาน์เตอร์ · หากต้องยื่นสถานทูตต่อในวันเดียวกัน ให้ตรวจเวลารับเรื่องของสถานทูตนั้นด้วย เพราะหลายแห่งปิดรับก่อนเที่ยง · " +
      CTA + " · " + TAIL,
    synonyms: ["นิติกรณ์ด่วน 400 บาท", "same day legalization bangkok", "รับรองกงสุลด่วนวันเดียว"],
    cluster: "speed",
  },
  {
    slug: "lgz-w2-เอกสารบริษัท",
    q: "เอกสารบริษัทต้องเตรียมอย่างไรก่อนเข้านิติกรณ์?",
    aShort:
      "ใช้หนังสือรับรองบริษัทที่คัดใหม่จากกรมพัฒนาธุรกิจการค้า พร้อมเอกสารประกอบ เช่น บอจ.5 หรือใบทะเบียนภาษีมูลค่าเพิ่ม แล้วแปลเป็นอังกฤษให้ตรงกับต้นฉบับทุกบรรทัดก่อนยื่น",
    aLong:
      "งานที่มักถูกตีกลับคือคำแปลชื่อบริษัทและวัตถุประสงค์ไม่ตรงกับที่จดทะเบียน หรือใช้หนังสือรับรองเก่าเกินกำหนดของปลายทาง · หากเอกสารเป็นเอกสารภายในบริษัท เช่น มติที่ประชุมหรือหนังสือมอบอำนาจกรรมการ ต้องผ่านทนายผู้ทำคำรับรองลายมือชื่อก่อนเข้าสายนิติกรณ์ · " +
      CTA + " · " + TAIL,
    synonyms: ["รับรองหนังสือรับรองบริษัทไปต่างประเทศ", "company affidavit legalization thailand", "เอกสารบริษัทยื่นกงสุล"],
    cluster: "documents",
  },
  {
    slug: "lgz-w2-เอกสารการศึกษา",
    q: "ปริญญาบัตรและทรานสคริปต์ต้องทำอะไรก่อนยื่นนิติกรณ์?",
    aShort:
      "ต้องให้สถาบันการศึกษาออกฉบับจริงหรือรับรองสำเนาถูกต้องพร้อมลายมือชื่อผู้มีอำนาจก่อน จากนั้นแปลเป็นอังกฤษแล้วจึงยื่นรับรองที่กรมการกงสุล",
    aLong:
      "บางมหาวิทยาลัยออกทรานสคริปต์ภาษาอังกฤษให้อยู่แล้ว ซึ่งช่วยลดขั้นตอนแปล แต่ยังต้องตรวจว่าลายมือชื่อผู้ลงนามเป็นผู้มีอำนาจตามที่สถาบันแจ้งไว้ · หากปลายทางเป็นหน่วยงานประเมินวุฒิ ให้ตรวจว่าเขาต้องการรับเอกสารโดยตรงจากสถาบันหรือรับจากผู้สมัคร เพราะสองแบบนี้เตรียมเอกสารต่างกัน · " +
      CTA + " · " + TAIL,
    synonyms: ["รับรองปริญญาบัตรไปต่างประเทศ", "transcript legalization thailand", "แปลรับรองวุฒิการศึกษา"],
    cluster: "documents",
  },
  {
    slug: "lgz-w2-ใช้กับวีซ่าแต่งงาน",
    q: "ยื่นวีซ่าแต่งงานหรือวีซ่าติดตามคู่สมรสต้องรับรองเอกสารชุดไหน?",
    aShort:
      "ชุดที่ใช้บ่อยคือ ใบรับรองความเป็นโสดหรือทะเบียนสมรส สูติบัตร ทะเบียนบ้าน และใบเปลี่ยนชื่อ-สกุลถ้ามี โดยแปลเป็นอังกฤษและผ่านนิติกรณ์ก่อนยื่นสถานทูตปลายทาง",
    aLong:
      "จุดพลาดที่พบบ่อยคือชื่อสะกดไม่ตรงกันระหว่างเอกสารแต่ละใบ เช่น สูติบัตรใช้ชื่อเดิมแต่หนังสือเดินทางใช้ชื่อใหม่ กรณีนี้ต้องแนบใบเปลี่ยนชื่อที่ผ่านการรับรองเข้าไปในชุดด้วย · ควรตรวจเช็กลิสต์ของสถานทูตปลายทางเป็นรายฉบับ เพราะบางประเทศกำหนดอายุเอกสารไม่เท่ากันในแต่ละรายการ · " +
      CTA + " · " + TAIL,
    synonyms: ["เอกสารวีซ่าแต่งงานต้องรับรองอะไร", "spouse visa document legalization thailand", "ใบโสดรับรองกงสุล"],
    cluster: "country",
  },
  {
    slug: "lgz-w2-เอกสารต่างประเทศใช้ในไทย",
    q: "เอกสารที่ออกจากต่างประเทศจะใช้ในไทยต้องรับรองอย่างไร?",
    aShort:
      "รับรองที่หน่วยงานของประเทศต้นทางก่อน แล้วให้สถานเอกอัครราชทูตหรือสถานกงสุลไทยในประเทศนั้นรับรอง จากนั้นนำเข้ามาแปลเป็นไทยและยื่นรับรองคำแปลที่กรมการกงสุล",
    aLong:
      "อีกเส้นทางที่ใช้ได้คือให้สถานทูตของประเทศต้นทางที่ตั้งอยู่ในประเทศไทยรับรองเอกสารนั้น แล้วจึงแปลไทยและยื่นนิติกรณ์ · หน่วยงานไทยที่รับเอกสารปลายทาง เช่น สำนักงานเขต กรมพัฒนาธุรกิจการค้า หรือศาล อาจกำหนดรูปแบบต่างกัน ควรถามก่อนเริ่มแปลเพื่อไม่ต้องทำซ้ำ · " +
      CTA + " · " + TAIL,
    synonyms: ["เอกสารต่างประเทศใช้ในไทย", "foreign document use in thailand legalization", "แปลไทยรับรองกงสุลเอกสารต่างชาติ"],
    cluster: "inbound",
  },
  {
    slug: "lgz-w2-e-notary-สแกน",
    q: "ใช้ไฟล์สแกนหรือเอกสารอิเล็กทรอนิกส์ยื่นนิติกรณ์ได้ไหม?",
    aShort:
      "งานนิติกรณ์อ้างอิงเอกสารกระดาษที่มีลายมือชื่อและตราประทับจริงเป็นหลัก ไฟล์สแกนใช้สำหรับตรวจสอบเบื้องต้นได้ แต่วันยื่นจริงยังต้องมีต้นฉบับ",
    aLong:
      "แนวปฏิบัติที่ประหยัดเวลาที่สุดคือส่งไฟล์สแกนให้ตรวจความถูกต้องของคำแปลและการสะกดชื่อก่อน แล้วจึงส่งต้นฉบับตามมา ทำให้แก้ปัญหาได้ก่อนเข้าคิว · หากปลายทางยอมรับเอกสารดิจิทัลที่มีการยืนยันออนไลน์ ให้ขอข้อกำหนดเป็นลายลักษณ์อักษรจากปลายทางไว้ประกอบ · " +
      CTA + " · " + TAIL,
    synonyms: ["ยื่นนิติกรณ์ออนไลน์ใช้ไฟล์สแกน", "electronic document legalization thailand", "e-notary กงสุล"],
    cluster: "online",
  },
  {
    slug: "lgz-w2-กี่ตราประทับ",
    q: "งานหนึ่งชุดต้องเสียค่าธรรมเนียมกี่ตราประทับ?",
    aShort:
      "นับตามจำนวนรายการเอกสารที่ขอรับรอง ไม่ใช่ตามจำนวนชุด เอกสารหนึ่งฉบับพร้อมคำแปลมักถูกนับเป็นรายการรับรองแยกกัน จึงควรนับจำนวนฉบับก่อนประเมินค่าธรรมเนียม",
    aLong:
      FEE +
      " ดังนั้นหากมีเอกสาร 5 ฉบับพร้อมคำแปล จำนวนตราประทับจะมากกว่า 5 · วิธีคุมงบคือคัดเฉพาะฉบับที่ปลายทางระบุในเช็กลิสต์จริง ๆ ไม่รับรองเผื่อทุกใบ · " +
      CTA + " · " + TAIL,
    synonyms: ["ค่าธรรมเนียมกงสุลนับยังไง", "mfa legalization fee per document", "นิติกรณ์กี่ตรา"],
    cluster: "price",
  },
  {
    slug: "lgz-w2-แก้ชื่อสะกดผิด",
    q: "ถ้าคำแปลสะกดชื่อผิดหลังผ่านนิติกรณ์แล้วต้องทำอย่างไร?",
    aShort:
      "ต้องแก้คำแปลและยื่นรับรองใหม่ ตราประทับเดิมแก้ไขไม่ได้ และปลายทางส่วนใหญ่ไม่รับเอกสารที่มีการขีดฆ่าแก้ไขด้วยลายมือ",
    aLong:
      "ต้นทุนของการแก้รอบสองคือค่าธรรมเนียมนิติกรณ์ใหม่และเวลาเข้าคิวใหม่ จึงคุ้มกว่ามากที่จะตรวจการสะกดชื่อเทียบหน้าหนังสือเดินทางทีละตัวอักษรก่อนยื่น · หากปลายทางเป็นหน่วยงานที่เคยเห็นชื่อสะกดแบบเดิม ให้แนบเอกสารอธิบายความเชื่อมโยงของชื่อทั้งสองแบบไปด้วย · " +
      CTA + " · " + TAIL,
    synonyms: ["สะกดชื่อผิดในคำแปล", "name spelling mismatch legalization", "แก้คำแปลหลังรับรอง"],
    cluster: "problem",
  },
  {
    slug: "lgz-w2-วางแผนเวลา",
    q: "ควรเริ่มทำเอกสารล่วงหน้ากี่วันก่อนวันนัดยื่น?",
    aShort:
      "แนวทางที่ปลอดภัยคือเริ่มก่อนวันนัด 3–4 สัปดาห์ เมื่อมีทั้งขั้นคัดเอกสารใหม่ แปล นิติกรณ์ และสถานทูตปลายทาง ส่วนงานที่ใช้เฉพาะนิติกรณ์อาจใช้เวลาน้อยกว่านั้นมาก",
    aLong:
      "ช่วงเวลาที่ควรเผื่อเพิ่มคือ ต้นเดือนและหลังวันหยุดยาวซึ่งคิวหนาแน่น รวมถึงช่วงที่สถานทูตปลายทางปิดทำการตามวันหยุดของประเทศตน · การรวบรวมต้นฉบับให้ครบก่อนเป็นตัวแปรที่ย่นเวลาได้มากที่สุด เพราะขั้นตอนราชการที่เหลือมีรอบเวลาค่อนข้างคงที่ · " +
      CTA + " · " + TAIL,
    synonyms: ["ควรเริ่มรับรองเอกสารล่วงหน้ากี่วัน", "legalization timeline thailand", "วางแผนเอกสารยื่นวีซ่า"],
    cluster: "speed",
  },
  {
    slug: "lgz-w2-เลือกผู้ให้บริการ",
    q: "ควรเลือกผู้ให้บริการเดินเรื่องนิติกรณ์อย่างไร?",
    aShort:
      "ดูสามข้อคือ แจกแจงค่าธรรมเนียมราชการแยกจากค่าบริการอย่างชัดเจน ระบุขั้นตอนและผู้รับผิดชอบเป็นลายลักษณ์อักษร และไม่ระบุผลการพิจารณาของหน่วยงานปลายทางล่วงหน้า",
    aLong:
      "ผู้ให้บริการที่รับปากผลการพิจารณาล่วงหน้าหรืออ้างช่องทางพิเศษกับหน่วยงานราชการเป็นสัญญาณอันตราย เพราะผลการพิจารณาเป็นอำนาจของหน่วยงาน · สิ่งที่ผู้ให้บริการที่ดีทำได้จริงคือช่วยตรวจเอกสารก่อนยื่น ลดรอบตีกลับ และเดินเรื่องแทนเพื่อประหยัดเวลาเดินทางของลูกค้า · " +
      CTA + " · " + TAIL,
    synonyms: ["เลือกบริษัทเดินเรื่องกงสุล", "how to choose legalization agent thailand", "บริการรับรองเอกสารน่าเชื่อถือ"],
    cluster: "compare",
  },
];
